import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { OrgUnitModel } from '../../../../types';
import { StyledTreeItem } from './styled-tree-item';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';

import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Add from '@mui/icons-material/Add';
import CorporateFare from '@mui/icons-material/CorporateFare';
import CorporateFareTwoTone from '@mui/icons-material/CorporateFareTwoTone';
import Search from '@mui/icons-material/Search';

interface OrgUnitsTreeProps {
  allOrgUnits: OrgUnitModel[];
  isLoading: boolean;
  selectedOrgUnitId: string;
  setSelectedOrgUnitId: React.Dispatch<React.SetStateAction<string>>;
  setOpenAddOrgUnitDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenEditOrgUnitDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenDeleteOrgUnitDialog: React.Dispatch<React.SetStateAction<boolean>>;
  directoryRoles: string[];
  limitedVisOrgList: string[];
  limitedVisEnabled: boolean;
}

export default function OrgUnitsTree({
  allOrgUnits,
  isLoading,
  selectedOrgUnitId,
  setSelectedOrgUnitId,
  setOpenAddOrgUnitDialog,
  setOpenEditOrgUnitDialog,
  setOpenDeleteOrgUnitDialog,
  directoryRoles,
  limitedVisOrgList,
  limitedVisEnabled,
}: OrgUnitsTreeProps) {
  const theme = useTheme();
  const [search, setSearch] = React.useState('');
  const [expandedNodes, setExpandedNodes] = React.useState<string[]>(['/']);

  const { primaryDomain } = useUserSettings();

  function buildTree(orgUnits: OrgUnitModel[]): OrgUnitModel[] {
    const orgUnitMap: Map<string, OrgUnitModel> = new Map();

    if (orgUnits.length === 0) {
      return [];
    }

    // Step 1: Organize OrgUnits into a map based on orgUnitId
    for (const orgUnit of orgUnits) {
      orgUnitMap.set(orgUnit.orgUnitPath, orgUnit);
    }

    // Step 2: Build the tree structure
    const tree: OrgUnitModel[] = [];
    for (const orgUnit of orgUnits) {
      const parentOrgUnitId = orgUnit.parentOrgUnitPath;

      if (parentOrgUnitId === '/') {
        // This is a root node
        tree.push(orgUnit);
      } else {
        // This is a child node, link it to its parent
        const parentOrgUnit = orgUnitMap.get(parentOrgUnitId!);
        if (parentOrgUnit) {
          if (!parentOrgUnit.children) {
            parentOrgUnit.children = [];
          }

          if (
            !parentOrgUnit.children.some(
              (child) => child.orgUnitPath === orgUnit.orgUnitPath
            )
          ) {
            parentOrgUnit.children.push(orgUnit);
          }
        }
      }
    }

    // Step 3: Return the tree
    return tree;
  }

  // recursive function to render folders
  const renderTree: (orgs: OrgUnitModel[]) => React.JSX.Element[] = (
    orgs: OrgUnitModel[]
  ) => {
    return orgs
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((org) => {
        return (
          <StyledTreeItem
            key={org.orgUnitPath}
            itemId={org.orgUnitPath}
            selectedOrgUnitId={selectedOrgUnitId}
            labelText={org.orgUnitPath.split('/').pop() || ''}
            labelIcon={CorporateFare}
            color={theme.palette.text.primary}
            bgColor={theme.palette.primary.light}
            setOpenEditOrgUnitDialog={setOpenEditOrgUnitDialog}
            setOpenDeleteOrgUnitDialog={setOpenDeleteOrgUnitDialog}
            directoryRoles={directoryRoles}
            limitedVisOrgList={limitedVisOrgList}
            limitedVisEnabled={limitedVisEnabled}
            hasChildren={Array.isArray(org.children)}
          >
            {Array.isArray(org.children) ? renderTree(org.children) : null}
          </StyledTreeItem>
        );
      });
  };

  const filterTree = (tree: OrgUnitModel[]): OrgUnitModel[] => {
    if (!search) {
      return tree;
    }

    return tree.reduce((filtered, orgUnit) => {
      const isMatch = orgUnit.name.toLowerCase().includes(search.toLowerCase());
      const hasChildren =
        Array.isArray(orgUnit.children) && orgUnit.children.length > 0;

      if (isMatch) {
        filtered.push({
          ...orgUnit,
          children: filterTree(orgUnit.children || []),
        });
      } else if (hasChildren) {
        const matchingChildren = filterTree(orgUnit.children || []);
        if (matchingChildren.length > 0) {
          filtered.push({
            ...orgUnit,
            children: matchingChildren,
          });
        }
      }

      return filtered;
    }, [] as OrgUnitModel[]);
  };

  const rootOrgUnit = allOrgUnits.find((org) => org.orgUnitPath === '/');

  if (isLoading) {
    return (
      <Stack gap={1} sx={{ p: 0.5 }}>
        <Skeleton variant="rounded" width={'100%'} height={30} />
        <Skeleton variant="rounded" width={'100%'} height={30} />
        <Skeleton variant="rounded" width={'100%'} height={30} />
        <Skeleton variant="rounded" width={'100%'} height={30} />
        <Skeleton variant="rounded" width={'100%'} height={30} />
        <Skeleton variant="rounded" width={'100%'} height={30} />
        <Skeleton variant="rounded" width={'100%'} height={30} />
        <Skeleton variant="rounded" width={'100%'} height={30} />
        <Skeleton variant="rounded" width={'100%'} height={30} />
        <Skeleton variant="rounded" width={'100%'} height={30} />
        <Skeleton variant="rounded" width={'100%'} height={30} />
        <Skeleton variant="rounded" width={'100%'} height={30} />
        <Skeleton variant="rounded" width={'100%'} height={30} />
      </Stack>
    );
  } else {
    return (
      <Stack sx={{ height: '100%' }}>
        <Stack flexDirection="row" gap={1} sx={{ mb: 1, px: 1 }}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flexGrow: 1 }}
            placeholder="Search..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Add Organizational Unit" arrow>
            <span>
              <IconButton
                onClick={() => setOpenAddOrgUnitDialog(true)}
                disabled={
                  !isAbleToSeeUIElement(
                    directoryRoles,
                    DirectoryRoles.AddOrgUnit.value
                  )
                }
              >
                <Add />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        <SimpleTreeView
          aria-label="org units"
          slots={{
            expandIcon: ArrowRightIcon,
            collapseIcon: ArrowDropDownIcon,
            endIcon: () => <div style={{ width: 24 }} />,
          }}
          sx={{
            height: '100%',
            overflowY: 'auto',
          }}
          onSelectedItemsChange={(_event, nodeId) => {
            if (nodeId) {
              setSelectedOrgUnitId(nodeId);
            }
          }}
          expandedItems={
            search !== ''
              ? allOrgUnits.map((org) => org.orgUnitPath)
              : expandedNodes
          }
          onExpandedItemsChange={(_event, nodes) => {
            setExpandedNodes(nodes);
          }}
          selectedItems={selectedOrgUnitId}
          multiSelect={false}
        >
          <StyledTreeItem
            itemId={'/'}
            selectedOrgUnitId={selectedOrgUnitId}
            labelText={rootOrgUnit?.name || primaryDomain}
            labelIcon={CorporateFareTwoTone}
            color={theme.palette.text.primary}
            bgColor={theme.palette.primary.light}
            setOpenEditOrgUnitDialog={setOpenEditOrgUnitDialog}
            setOpenDeleteOrgUnitDialog={setOpenDeleteOrgUnitDialog}
            directoryRoles={directoryRoles}
            limitedVisOrgList={limitedVisOrgList}
            limitedVisEnabled={limitedVisEnabled}
          >
            {renderTree(filterTree(buildTree(allOrgUnits)))}
          </StyledTreeItem>
        </SimpleTreeView>
      </Stack>
    );
  }
}
